import React from 'react';
import { DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { MarkdownRenderer } from './MarkdownRenderer.tsx';

interface TableData {
  headers: string[];
  rows: (string | number)[][];
}

interface TabData {
  table1: TableData;
  table2: TableData;
  table3: TableData;
}

interface EventAnalysisDashboardProps {
  activeTab: string;
  sessionData?: any;
  vendorTotalsFromSummary?: Record<string, number>;
  transformedData: any;
}

// Enhanced table extraction with proper heading detection
function extractTablesWithHeadings(markdown: string): Array<{ heading: string, table: TableData }> {
  const tables: Array<{ heading: string, table: TableData }> = [];
  
  // Split markdown into lines for processing
  const lines = markdown.split('\n');
  let currentHeading = '';
  let tableCount = 0;
  
  // Extract JSON tables first
  const jsonBlocks = extractJsonTables(markdown);
  if (jsonBlocks.length > 0) {
    return jsonBlocks;
  }
  
  // Process markdown tables
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Check for markdown headings (### Table Title, ## Table Title, etc.)
    const headingMatch = line.match(/^#{1,6}\s+(.+)/);
    if (headingMatch) {
      currentHeading = headingMatch[1].trim();
      continue;
    }
    
    // Check for bold text that might be a table title
    const boldMatch = line.match(/^\*\*(.+)\*\*$/);
    if (boldMatch) {
      currentHeading = boldMatch[1].trim();
      continue;
    }
    
    // Check for specific section headers from the separate repo
    const sectionHeaders = [
      'Key Metrics',
      'Total Cost Comparison', 
      'Cost Category Breakdown',
      'Price Outliers',
      'Performance Matrix',
      'Item Matching Results',
      'Price Position Summary',
      'Complete Item Comparison',
      'Negotiation Opportunities',
      'Missing Benchmarks'
    ];
    
    for (const header of sectionHeaders) {
      if (line.includes(header)) {
        currentHeading = header;
        break;
      }
    }
    
    // Check for table start (markdown table format)
    if (line.includes('|') && line.split('|').length > 2) {
      const tableResult = parseMarkdownTable(lines, i);
      if (tableResult) {
        tableCount++;
        const finalHeading = currentHeading || 
          (line.includes('Common Items') ? 'Common Items Available from All Vendors' :
           line.includes('Significant Price') ? 'Items with Significant Price Variations' :
           line.includes('Vendor-Specific') ? 'Vendor-Specific Items' :
           line.includes('Below Market') ? 'Items Priced Below Market Average' :
           line.includes('Above Market') ? 'Items Priced Above Market Average' :
           line.includes('Market Reference') ? 'Items with No Market Reference Data' :
           line.includes('Master Comparison Table') ? 'Master Comparison Table' :
           line.includes('Key Metrics') ? 'Key Metrics' :
           line.includes('Total Cost Comparison') ? 'Total Cost Comparison' :
           line.includes('Cost Category Breakdown') ? 'Cost Category Breakdown' :
           line.includes('Price Outliers') ? 'Price Outliers' :
           line.includes('Performance Matrix') ? 'Performance Matrix' :
           line.includes('Item Matching Results') ? 'Item Matching Results' :
           line.includes('Price Position Summary') ? 'Price Position Summary' :
           line.includes('Complete Item Comparison') ? 'Complete Item Comparison' :
           line.includes('Negotiation Opportunities') ? 'Negotiation Opportunities' :
           line.includes('Missing Benchmarks') ? 'Missing Benchmarks' :
           `Table ${tableCount}`);
        
        tables.push({
          heading: finalHeading,
          table: tableResult.table
        });
        
        i = tableResult.endIndex;
        currentHeading = ''; // Reset heading after use
      }
    }
  }
  
  return tables;
}

// Extract JSON tables from markdown
function extractJsonTables(markdown: string): Array<{ heading: string, table: TableData }> {
  const tables: Array<{ heading: string, table: TableData }> = [];
  
  // Extract JSON blocks
  const jsonBlocks: string[] = [];
  let braceCount = 0;
  let startIndex = -1;
  
  for (let i = 0; i < markdown.length; i++) {
    if (markdown[i] === '{') {
      if (braceCount === 0) {
        startIndex = i;
      }
      braceCount++;
    } else if (markdown[i] === '}') {
      braceCount--;
      if (braceCount === 0 && startIndex !== -1) {
        jsonBlocks.push(markdown.substring(startIndex, i + 1) as string);
        startIndex = -1;
      }
    }
  }
  
  for (const jsonStr of jsonBlocks) {
    try {
      const parsedJson = JSON.parse(jsonStr);
      
      // Extract tables from the parsed JSON with better naming
      Object.entries(parsedJson).forEach(([key, value]) => {
        if (value && typeof value === 'object' && 'headers' in value && 'rows' in value) {
          const tableData = value as TableData;
          if (Array.isArray(tableData.headers) && Array.isArray(tableData.rows)) {
            // Generate meaningful table names
            const tableName = generateTableName(key, tableData.headers);
            tables.push({
              heading: tableName,
              table: tableData
            });
          }
        }
      });
    } catch (error) {
      console.error('JSON parsing error:', error);
    }
  }
  
  return tables;
}

// Generate meaningful table names based on key and headers
function generateTableName(key: string, headers: string[]): string {
  // Remove table1, table2, etc. prefixes
  const cleanKey = key.replace(/^table\d+$/i, '').trim();
  
  if (cleanKey) {
    return cleanKey;
  }
  
  // Check for specific sections from the separate repo
  const headerText = headers.join(' ').toLowerCase();
  
  if (headerText.includes('metric') && headerText.includes('value') && headerText.includes('status')) {
    return 'Key Metrics';
  }
  
  if (headerText.includes('vendor') && headerText.includes('total cost') && headerText.includes('vs benchmark')) {
    return 'Total Cost Comparison';
  }
  
  if (headerText.includes('category') && headerText.includes('best value')) {
    return 'Cost Category Breakdown';
  }
  
  if (headerText.includes('item') && headerText.includes('vendor') && headerText.includes('price') && headerText.includes('flag')) {
    return 'Price Outliers';
  }
  
  if (headerText.includes('criteria') && headerText.includes('weight') && headerText.includes('score')) {
    return 'Performance Matrix';
  }
  
  if (headerText.includes('item category') && headerText.includes('matched benchmark') && headerText.includes('confidence')) {
    return 'Item Matching Results';
  }
  
  if (headerText.includes('position') && headerText.includes('item count') && headerText.includes('percentage')) {
    return 'Price Position Summary';
  }
  
  if (headerText.includes('item') && headerText.includes('category') && headerText.includes('benchmark') && headerText.includes('match quality')) {
    return 'Complete Item Comparison';
  }
  
  if (headerText.includes('priority') && headerText.includes('current best') && headerText.includes('target price') && headerText.includes('potential saving')) {
    return 'Negotiation Opportunities';
  }
  
  if (headerText.includes('item') && headerText.includes('category') && headerText.includes('current quotes') && headerText.includes('recommendation')) {
    return 'Missing Benchmarks';
  }
  
  // Infer table name from headers
  if (headers.some(h => h.toLowerCase().includes('market'))) {
    if (headers.some(h => h.toLowerCase().includes('below'))) {
      return 'Items Priced Below Market Average';
    }
    if (headers.some(h => h.toLowerCase().includes('above'))) {
      return 'Items Priced Above Market Average';
    }
    return 'Market Comparison Analysis';
  }
  
  if (headers.some(h => h.toLowerCase().includes('difference'))) {
    return 'Items with Significant Price Variations';
  }
  
  if (headers.some(h => h.toLowerCase().includes('vendor')) && headers.length <= 4) {
    return 'Vendor-Specific Items';
  }
  
  // Check if this looks like a master comparison table
  if (headers.length > 8 && headers.some(h => h.toLowerCase().includes('price'))) {
    return 'Master Comparison Table';
  }
  
  if (headers.some(h => h.toLowerCase().includes('price'))) {
    return 'Vendor Price Comparison';
  }
  
  return 'Analysis Table';
}

// Parse markdown table format
function parseMarkdownTable(lines: string[], startIndex: number): { table: TableData, endIndex: number } | null {
  const headerLine = lines[startIndex];
  if (!headerLine.includes('|')) return null;
  
  // Parse headers
  const headers = headerLine.split('|')
    .map(h => h.trim())
    .filter(h => h.length > 0);
  
  if (headers.length === 0) return null;
  
  // Skip separator line
  let currentIndex = startIndex + 1;
  if (currentIndex < lines.length && lines[currentIndex].includes('---')) {
    currentIndex++;
  }
  
  // Parse rows
  const rows: (string | number)[][] = [];
  while (currentIndex < lines.length) {
    const line = lines[currentIndex].trim();
    if (!line || !line.includes('|')) break;
    
    const cells = line.split('|')
      .map(cell => cell.trim())
      .filter(cell => cell.length > 0);
    
    if (cells.length === headers.length) {
      rows.push(cells);
    }
    
    currentIndex++;
  }
  
  return {
    table: { headers, rows },
    endIndex: currentIndex - 1
  };
}

// Extract vendor legend mapping from markdown (e.g., Vendor C: CFI)
function extractVendorLegendMap(markdown: string): Record<string, string> {
  const legendMap: Record<string, string> = {};
  const legendRegex = /Vendor\s+([A-Z]):\s*([^\n]+)/g;
  let match;
  while ((match = legendRegex.exec(markdown)) !== null) {
    legendMap[match[1]] = match[2].trim();
  }
  return legendMap;
}

// Enhanced vendor name extraction that handles single vendor scenarios
function extractVendorNames(tables: Array<{ heading: string, table: TableData }>, markdown: string): string[] {
  // First check if this is a single vendor scenario
  const singleVendorMatch = markdown.match(/Total Quote Value:\s*₹([\d,]+)/);
  if (singleVendorMatch) {
    // This is a single vendor scenario, extract vendor name
    let vendorName = 'Unknown Vendor';
    
    const vendorNameMatches = [
      markdown.match(/vendor quote \(([^)]+)\)/i),
      markdown.match(/Analysis of ([^Benchmark\s]+)(?:\s+Benchmark)?\s+Quote/i),
      markdown.match(/Event:\s*Conference at ([^,]+)/i),
      markdown.match(/Venue:\s*([^,\n]+)/i)
    ];
    
    for (const match of vendorNameMatches) {
      if (match && match[1]) {
        const extracted = match[1].trim();
        if (extracted && !extracted.toLowerCase().includes('benchmark')) {
          vendorName = extracted;
          break;
        }
      }
    }
    
    return [vendorName];
  }
  
  // Multi-vendor scenario - find the first table with price columns
  const priceTable = tables.find(t =>
    t.table.headers.some(h =>
      /price/i.test(h) &&
      !/market|difference|variance|best|worst|savings|premium/i.test(h)
    )
  );
  
  if (priceTable) {
    // Extract vendor names from price headers
    return priceTable.table.headers
      .filter(h =>
        /price/i.test(h) &&
        !/market|difference|variance|best|worst|savings|premium/i.test(h)
      )
      .map(h => {
        // Extract the word/phrase before 'Price' (e.g., 'Shobiz Price' => 'Shobiz')
        const match = h.match(/(.+?)\s*Price/i);
        if (match && match[1]) {
          return match[1].replace(/vendor\s*[a-z]?/i, '').replace(/^[\(\)]/g, '').replace(/\s+/g, ' ').trim();
        }
        // Fallback: remove 'Price' and clean up
        return h.replace(/price/i, '').replace(/vendor\s*[a-z]?/i, '').replace(/^[\(\)]/g, '').replace(/\s+/g, ' ').trim();
      });
  }
  // Fallback: use a table with 'Vendor' and 'Total Cost' columns
  const totalCostTable = tables.find(t => {
    const headersLower = t.table.headers.map(h => h.toLowerCase());
    return headersLower.includes('vendor') && headersLower.some(h => h.includes('total cost'));
  });
  if (totalCostTable) {
    const vendorColIdx = totalCostTable.table.headers.findIndex(h => h.toLowerCase() === 'vendor');
    if (vendorColIdx >= 0) {
      const names = totalCostTable.table.rows
        .map(r => (r[vendorColIdx] ?? '').toString().trim())
        .filter(n => n && n.toLowerCase() !== 'vendor');
      return Array.from(new Set(names));
    }
  }
  return [];
}

// Enhanced vendor totals extraction that handles single vendor scenarios
function extractVendorTotals(tables: Array<{ heading: string, table: TableData }>, markdown: string): Record<string, number> {
  let vendorTotals: Record<string, number> = {};

  // First, check if this is a single vendor scenario
  const singleVendorMatch = markdown.match(/Total Quote Value:\s*₹([\d,]+)/);
  if (singleVendorMatch) {
    // This is a single vendor scenario
    let vendorName = 'Unknown Vendor';
    
    const vendorNameMatches = [
      markdown.match(/vendor quote \(([^)]+)\)/i),
      markdown.match(/Analysis of ([^Benchmark\s]+)(?:\s+Benchmark)?\s+Quote/i),
      markdown.match(/Event:\s*Conference at ([^,]+)/i),
      markdown.match(/Venue:\s*([^,\n]+)/i)
    ];
    
    for (const match of vendorNameMatches) {
      if (match && match[1]) {
        const extracted = match[1].trim();
        if (extracted && !extracted.toLowerCase().includes('benchmark')) {
          vendorName = extracted;
          break;
        }
      }
    }
    
    const totalValue = parseInt(singleVendorMatch[1].replace(/,/g, ''));
    if (!isNaN(totalValue)) {
      vendorTotals[vendorName] = totalValue;
      return vendorTotals;
    }
  }

  // Multi-vendor scenario - find the table with price data
  const priceTable = tables.find(t => 
    t.table.headers.some(h => /price/i.test(h) && !/market|difference/i.test(h))
  );

  if (priceTable) {
    const { headers, rows } = priceTable.table;
    // Find price columns
    const priceColumns = headers
      .map((h, i) => ({ header: h, index: i }))
      .filter(({ header }) => /price/i.test(header) && !/market|difference|variance|best|worst|savings|premium/i.test(header));
    
    // Calculate totals for each vendor
    for (const { header, index } of priceColumns) {
      let vendorName = header
        .replace(/price/i, '')
        .replace(/vendor\s+[a-z]/i, '')
        .trim();
      vendorName = vendorName
        .replace(/^[a-z]\s+\(/i, '(')
        .replace(/^[\(\)]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      if (!vendorName) continue;
      
      let total = 0;
      for (const row of rows) {
        const cellValue = row[index];
        if (cellValue) {
          const numericValue = typeof cellValue === 'string' 
            ? parseInt((cellValue as string).replace(/[^\d-]/g, ''))
            : (cellValue as number);
          if (!isNaN(numericValue) && isFinite(numericValue)) {
            total += numericValue;
          }
        }
      }
      if (total > 0) {
        vendorTotals[vendorName] = total;
      }
    }
  }

  // Fallback: infer totals from a 'Total Cost Comparison' table
  if (Object.keys(vendorTotals).length === 0) {
    const totalCostTable = tables.find(t => {
      const headersLower = t.table.headers.map(h => h.toLowerCase());
      return headersLower.includes('vendor') && headersLower.some(h => h.includes('total cost'));
    });
    if (totalCostTable) {
      const vendorColIdx = totalCostTable.table.headers.findIndex(h => h.toLowerCase() === 'vendor');
      const totalCostColIdx = totalCostTable.table.headers.findIndex(h => h.toLowerCase().includes('total cost'));
      if (vendorColIdx >= 0 && totalCostColIdx >= 0) {
        totalCostTable.table.rows.forEach(row => {
          const name = (row[vendorColIdx] ?? '').toString().trim();
          const raw = row[totalCostColIdx];
          const value = typeof raw === 'string' ? parseInt((raw as string).replace(/[^\d-]/g, '')) : (raw as number);
          if (name && !isNaN(value)) {
            vendorTotals[name] = value;
          }
        });
      }
    }
  }

  // If no totals found, try to extract from Executive Summary markdown
  if (Object.keys(vendorTotals).length === 0 && markdown) {
    // Regex for lines like: - **Vendor A (Shobiz)**: ₹14,49,470
    const regex = /- \*\*Vendor [A-Z] \(([^)]+)\)\*\*:\s*₹([\d,]+)/g;
    let match;
    while ((match = regex.exec(markdown)) !== null) {
      const vendorName = match[1].trim();
      const value = parseInt(match[2].replace(/,/g, ''));
      if (vendorName && !isNaN(value)) {
        vendorTotals[vendorName] = value;
      }
    }
  }

  // Map 'Unnamed' to legend if available
  const legendMap = extractVendorLegendMap(markdown);
  const remappedTotals: Record<string, number> = {};
  Object.entries(vendorTotals).forEach(([name, value], idx) => {
    if (/^Unnamed$/i.test(name)) {
      const letter = String.fromCharCode(65 + idx); // A, B, C, ...
      if (legendMap[letter]) {
        remappedTotals[legendMap[letter]] = value;
        return;
      }
    }
    remappedTotals[name] = value;
  });

  return remappedTotals;
}

const StatCard = ({ title, value, subtitle, icon: Icon, trend, className = "" }: any) => (
  <div className={`bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 ${className} font-['Work_Sans']`}>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-blue-50 rounded-lg">
          <span className="h-5 w-5 text-blue-600 text-xl font-bold">₹</span>
        </div>
        <span className="text-sm font-medium text-gray-600">{title}</span>
      </div>
      {trend && (
        <div className={`flex items-center space-x-1 ${trend > 0 ? 'text-red-500' : 'text-green-500'}`}>
          {trend > 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          <span className="text-sm font-medium">{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
    <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
    {subtitle && <div className="text-sm text-gray-500">{subtitle}</div>}
  </div>
);

const DynamicTable = ({ headers, rows }: TableData) => {
  if (!Array.isArray(headers) || !Array.isArray(rows)) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto my-6 font-['Work_Sans'] p-4 text-red-500">
        Invalid table data.
      </div>
    );
  }
  if (headers.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto my-6 font-['Work_Sans'] p-4 text-gray-500">
        No columns to display.
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto my-6 font-['Work_Sans']">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((header, i) => (
              <th key={i} className="px-6 py-4 text-left font-semibold text-gray-900 whitespace-nowrap">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50">
              {Array.isArray(row)
                ? row.map((cell, j) => (
                    <td key={j} className="px-6 py-4 text-gray-900 whitespace-nowrap">{cell}</td>
                  ))
                : <td colSpan={headers.length} className="px-6 py-4 text-gray-500">Invalid row data</td>
              }
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Update VendorBarChart to accept vendorTotalsFromSummary and use it for the bar graph
const VendorBarChart = ({ vendorTotalsFromSummary }: { vendorTotalsFromSummary?: Record<string, number> }) => {
  const vendorTotals = vendorTotalsFromSummary || {};
  const vendors = Object.keys(vendorTotals);
  const values = Object.values(vendorTotals);
  const colors = ['#10b981', '#6366f1', '#f59e0b', '#ef4444', '#a21caf', '#f59e42'];
  const chartHeight = 340;
  const barWidth = 80;
  const barGap = 60;
  const chartWidth = vendors.length * (barWidth + barGap) + 80;
  const maxValue = Math.max(...values, 1);

  if (vendors.length === 0) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 my-6 font-['Work_Sans']">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Quoted Value by Vendor</h3>
        <div className="text-gray-500">No vendor totals available.</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 my-6 font-['Work_Sans']">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Quoted Value by Vendor</h3>
      <div className="w-full overflow-x-auto">
        <div style={{ minWidth: chartWidth }}>
          <svg width={chartWidth} height={chartHeight} style={{ minWidth: chartWidth }}>
          {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
            <g key={i}>
              <line
                x1={40}
                x2={chartWidth - 40}
                y1={chartHeight - 40 - t * (chartHeight - 80)}
                y2={chartHeight - 40 - t * (chartHeight - 80)}
                stroke="#e5e7eb"
                strokeDasharray="4 2"
              />
              <text
                x={20}
                y={chartHeight - 40 - t * (chartHeight - 80) + 5}
                fontSize="13"
                fill="#888"
                textAnchor="end"
              >
                ₹{Math.round(maxValue * t).toLocaleString('en-IN')}
              </text>
            </g>
          ))}
          {vendors.map((vendor, i) => {
            const amt = vendorTotals[vendor];
            const barHeight = (amt / maxValue) * (chartHeight - 80);
            return (
              <g key={vendor}>
                <rect
                  x={80 + i * (barWidth + barGap)}
                  y={chartHeight - 40 - barHeight}
                  width={barWidth}
                  height={barHeight}
                  fill={colors[i % colors.length]}
                  rx={8}
                />
                <g>
                  <title>{vendor}</title>
                  <text
                    x={80 + i * (barWidth + barGap) + barWidth / 2}
                    y={chartHeight - 10}
                    textAnchor="middle"
                    fontSize="13"
                    fill="#222"
                    fontWeight="bold"
                    style={{ pointerEvents: 'all' }}
                  >
                    {vendor.length > 16
                      ? <tspan x={80 + i * (barWidth + barGap) + barWidth / 2} dy="-7">{vendor.slice(0, Math.ceil(vendor.length/2))}</tspan>
                        : null}
                    {vendor.length > 16
                      ? <tspan x={80 + i * (barWidth + barGap) + barWidth / 2} dy="15">{vendor.slice(Math.ceil(vendor.length/2))}</tspan>
                      : vendor}
                  </text>
                </g>
                <text
                  x={80 + i * (barWidth + barGap) + barWidth / 2}
                  y={chartHeight - 50 - barHeight}
                  textAnchor="middle"
                  fontSize="15"
                  fill="#444"
                  fontWeight="bold"
                >
                  ₹{amt.toLocaleString('en-IN')}
                </text>
              </g>
            );
          })}
        </svg>
        </div>
      </div>
    </div>
  );
};

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

// Custom label component for pie charts to improve readability
const renderCustomizedLabel = (props: any) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent, category, value } = props;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius * 1.1;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
  if (percent < 0.05) return null;
  
  return (
    <text 
      x={x} 
      y={y} 
      fill="#555"
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize="13"
      fontWeight="500"
    >
      {category} (₹{value.toLocaleString()})
    </text>
  );
};

// Enhanced function to get vendor breakdown for single vendor scenarios
function getVendorBreakdown(tables: Array<{ heading: string, table: TableData }>, vendorName: string, markdown: string) {
  // First check if this is a single vendor scenario
  const singleVendorMatch = markdown.match(/Total Quote Value:\s*₹([\d,]+)/);
  if (singleVendorMatch) {
    // For single vendor, extract cost breakdown from markdown
    const categoryBreakdown: Array<{ category: string, value: number, color: string }> = [];
    
    // Look for cost breakdown patterns in markdown
    const breakdownRegex = /(\d+)\.\s*([^:]+):\s*₹([\d,]+)/g;
    let match;
    let colorIndex = 0;
    
    while ((match = breakdownRegex.exec(markdown)) !== null) {
      const category = match[2].trim();
      const value = parseInt(match[3].replace(/,/g, ''));
      
      if (!isNaN(value) && value > 0) {
        categoryBreakdown.push({
          category,
          value,
          color: COLORS[colorIndex % COLORS.length]
        });
        colorIndex++;
      }
    }
    
    if (categoryBreakdown.length > 0) {
      return categoryBreakdown;
    }
  }

  // Multi-vendor scenario - find the table with this vendor's data
  const priceTable = tables.find(t => 
    t.table.headers.some(h => h.toLowerCase().includes(vendorName.toLowerCase()))
  );

  if (!priceTable) return [];

  const vendorIdx = priceTable.table.headers.findIndex(
    h => h.replace(/ Price/i, '').trim().toLowerCase() === vendorName.trim().toLowerCase()
  );
  
  if (vendorIdx === -1) return [];

  return priceTable.table.rows.map((row, i) => {
    const item = row[0] as string;
    const price = typeof row[vendorIdx] === 'string' ? parseInt((row[vendorIdx] as string).replace(/[^\d]/g, '')) : row[vendorIdx];
    return {
      category: item,
      value: isNaN(price as number) ? 0 : (price as number),
      color: COLORS[i % COLORS.length]
    };
  }).filter(d => d.value > 0);
}

// Enhanced VendorPieCharts component that works with single vendor scenarios
const VendorPieCharts = ({ tabData, tables, markdown }: { tabData?: TabData, tables?: Array<{ heading: string, table: TableData }>, markdown?: string }) => {
  
  let vendorNames: string[] = [];
  let priceTable: TableData | null = null;
  
  // Check if this is a single vendor scenario
  const singleVendorMatch = markdown?.match(/Total Quote Value:\s*₹([\d,]+)/);
  if (singleVendorMatch) {
    // Single vendor scenario
    let vendorName = 'Unknown Vendor';
    
    const vendorNameMatches = [
      markdown?.match(/vendor quote \(([^)]+)\)/i),
      markdown?.match(/Analysis of ([^Benchmark\s]+)(?:\s+Benchmark)?\s+Quote/i),
      markdown?.match(/Event:\s*Conference at ([^,]+)/i),
      markdown?.match(/Venue:\s*([^,\n]+)/i)
    ];
    
    for (const match of vendorNameMatches) {
      if (match && match[1]) {
        const extracted = match[1].trim();
        if (extracted && !extracted.toLowerCase().includes('benchmark')) {
          vendorName = extracted;
          break;
        }
      }
    }
    
    vendorNames = [vendorName];
  } else {
    // Multi-vendor scenario
    // Try to get data from tabData first (legacy support)
    if (tabData && tabData.table1) {
      vendorNames = tabData.table1.headers
        .filter(h => h.toLowerCase().includes('price') && !h.toLowerCase().includes('market') && !/price difference/i.test(h))
        .map(h => h.replace(/ Price/i, '').trim());
      priceTable = tabData.table1;
    } 
    // Otherwise use extracted tables
    else if (tables && tables.length > 0) {
      const firstPriceTable = tables.find(t => 
        t.table.headers.some(h => /price/i.test(h) && !/market|difference/i.test(h))
      );
      
      if (firstPriceTable) {
        vendorNames = firstPriceTable.table.headers
          .filter(h => h.toLowerCase().includes('price') && !h.toLowerCase().includes('market') && !/price difference/i.test(h))
          .map(h => h.replace(/ Price/i, '').trim());
        priceTable = firstPriceTable.table;
      }
    }
  }
  
  if (vendorNames.length === 0) {
    return (
      <div className="mb-10">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Vendor Cost Breakdown Analysis</h2>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="text-gray-500">No price data available for breakdown charts.</div>
        </div>
      </div>
    );
  }

  // Get data for each vendor
  const vendorData = vendorNames.map((vendorName) => {
    const data = getVendorBreakdown(tables || [], vendorName, markdown || '');
    const total = data.reduce((sum, item) => sum + item.value, 0);
    return {
      name: vendorName,
      data,
      total
    };
  }).filter(v => v.data.length > 0);

  if (vendorData.length === 0) {
    return (
      <div className="mb-10">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Vendor Cost Breakdown Analysis</h2>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="text-gray-500">No valid price data found for breakdown charts.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Vendor Cost Breakdown Analysis</h2>
      
      {/* Mobile layout */}
      <div className="block md:hidden">
        {vendorData.map((vendor, idx) => (
          <div key={idx} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
            <h3 className="text-lg font-semibold text-primary-900 mb-3">{vendor.name} Cost Breakdown</h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={vendor.data}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={renderCustomizedLabel}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {vendor.data.map((entry, index) => (
                    <Cell key={`cell-${idx}-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`₹${value.toLocaleString()}`, 'Cost']}
                  contentStyle={{ borderRadius: '8px', padding: '10px', border: '1px solid #e2e8f0' }}
                />
              </PieChart>
            </ResponsiveContainer>
            
            <div className="grid grid-cols-1 gap-y-2 mt-2">
              {vendor.data.map((entry, index) => (
                <div key={index} className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded-sm mr-2" 
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className="text-sm text-gray-700">
                    {entry.category}: ₹{entry.value.toLocaleString('en-IN')} 
                    ({((entry.value / vendor.total) * 100).toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Desktop layout */}
      <div className="hidden md:block">
        <div className="grid grid-cols-3 gap-6">
          {vendorData.map((vendor, idx) => (
            <div key={idx} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-primary-900 mb-4">{vendor.name} Cost Breakdown</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <Pie
                    data={vendor.data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {vendor.data.map((entry, index) => (
                      <Cell key={`cell-${idx}-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Cost']}
                    contentStyle={{ borderRadius: '8px', padding: '10px', border: '1px solid #e2e8f0' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              
              <div className="mt-4 max-h-[150px] overflow-y-auto pl-2 pr-1 py-2">
                {vendor.data.map((entry, index) => (
                  <div key={index} className="flex items-center mb-2">
                    <div 
                      className="min-w-[12px] h-3 rounded-sm mr-2" 
                      style={{ backgroundColor: entry.color }}
                    ></div>
                    <span className="text-xs text-gray-700 truncate">
                      {entry.category}: ₹{entry.value.toLocaleString('en-IN')} 
                      ({((entry.value / vendor.total) * 100).toFixed(0)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Function to extract specific summary sections from markdown
function extractSummarySections(markdown: string): Record<string, string> {
  const sections: Record<string, string> = {};
  
  // Define the sections we want to extract
  const sectionNames = [
    'Executive Summary',
    'Recommendations', 
    'Decision Summary',
    'Vendor Profiles',
    'Analysis Metadata'
  ];
  
  // Split markdown into lines
  const lines = markdown.split('\n');
  let currentSection = '';
  let currentContent: string[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check if this line starts a new section (more flexible matching)
    const isSectionHeader = sectionNames.some(sectionName => {
      const trimmedLine = line.trim();
      return trimmedLine === `# ${sectionName}` || 
             trimmedLine === `## ${sectionName}` ||
             trimmedLine.startsWith(`# ${sectionName}`) || 
             trimmedLine.startsWith(`## ${sectionName}`) ||
             trimmedLine.includes(`# ${sectionName}`) ||
             trimmedLine.includes(`## ${sectionName}`);
    });
    
    // Check for sections we want to skip (stop processing when we hit these)
    const skipSections = [
      'Price Analysis',
      'Vendor Comparison', 
      'Benchmark Analysis',
      'Detailed Item Analysis'
    ];
    
    const isSkipSection = skipSections.some(sectionName => {
      const trimmedLine = line.trim();
      return trimmedLine === `# ${sectionName}` || 
             trimmedLine === `## ${sectionName}` ||
             trimmedLine.startsWith(`# ${sectionName}`) || 
             trimmedLine.startsWith(`## ${sectionName}`);
    });
    
    if (isSkipSection) {
      // Save current section and stop processing
      if (currentSection && currentContent.length > 0) {
        sections[currentSection] = currentContent.join('\n');
      }
      currentSection = '';
      currentContent = [];
      // Don't break - continue processing to find Vendor Profiles and Analysis Metadata
    }
    
    if (isSectionHeader) {
      // Save previous section content if exists
      if (currentSection && currentContent.length > 0) {
        sections[currentSection] = currentContent.join('\n');
      }
      
      // Start new section
      currentSection = sectionNames.find(sectionName => {
        const trimmedLine = line.trim();
        return trimmedLine === `# ${sectionName}` || 
               trimmedLine === `## ${sectionName}` ||
               trimmedLine.startsWith(`# ${sectionName}`) || 
               trimmedLine.startsWith(`## ${sectionName}`) ||
               trimmedLine.includes(`# ${sectionName}`) ||
               trimmedLine.includes(`## ${sectionName}`);
      }) || '';
      currentContent = [line];
    } else if (currentSection && (line.trim() || currentContent.length > 0)) {
      // For Executive Summary, skip Vendor Legend and Key Metrics subsections
      if (currentSection === 'Executive Summary') {
        const trimmedLine = line.trim();
        if (trimmedLine === '## Vendor Legend' || 
            trimmedLine === '## Key Metrics' ||
            trimmedLine.startsWith('## Vendor Legend') ||
            trimmedLine.startsWith('## Key Metrics')) {
          // Skip this subsection
          continue;
        }
      }
      
      // Add line to current section content (include empty lines for formatting)
      currentContent.push(line);
    }
  }
  
  // Save the last section
  if (currentSection && currentContent.length > 0) {
    sections[currentSection] = currentContent.join('\n');
  }
    
  // Fallback: if Vendor Profiles or Analysis Metadata are missing, search more broadly
  const missingSections = ['Vendor Profiles', 'Analysis Metadata'].filter(
    section => !sections[section]
  );
  
  if (missingSections.length > 0) {
    
    for (const missingSection of missingSections) {
      const sectionIndex = lines.findIndex(line => 
        line.trim().includes(missingSection) && 
        (line.trim().startsWith('#') || line.trim().startsWith('##'))
      );
      
      if (sectionIndex !== -1) {
        let sectionContent = [lines[sectionIndex]];
        let i = sectionIndex + 1;
        
        // Collect content until next section or end
        while (i < lines.length) {
          const nextLine = lines[i];
          if (nextLine.trim().startsWith('#') && nextLine.trim() !== lines[sectionIndex].trim()) {
            break;
          }
          sectionContent.push(nextLine);
          i++;
        }
        
        sections[missingSection] = sectionContent.join('\n');
      }
    }
  }
  
  return sections;
}

const EventAnalysisDashboard: React.FC<EventAnalysisDashboardProps> = ({ 
  activeTab, 
  sessionData, 
  vendorTotalsFromSummary, 
  transformedData 
}) => {
  // Extract markdown content for the current tab
  const tabMarkdownArr = sessionData?.output?.content?.[activeTab] || [];
  const tabMarkdown = tabMarkdownArr.find((item: any) => item.type === 'markdown')?.content || '';
  
  // Extract summary sections
  let summarySections = extractSummarySections(tabMarkdown);
  
  // Fallback: if no sections found in current tab, try to extract from all content
  if (Object.keys(summarySections).length === 0 && sessionData?.output?.content) {
    let allMarkdownContent = '';
    
    Object.entries(sessionData.output.content).forEach(([tabName, content]) => {
      if (Array.isArray(content)) {
        content.forEach((item: any) => {
          if (item.type === 'markdown' && item.content) {
            allMarkdownContent += item.content + '\n\n';
          }
        });
      }
    });
    
    if (allMarkdownContent) {
      summarySections = extractSummarySections(allMarkdownContent);
    }
  }
  
  // Extract tables with proper headings using enhanced logic
  const tablesToRender = extractTablesWithHeadings(tabMarkdown);
  
  // Fallback: if no tables found in markdown, try to use transformedData.allMarkdownTables
  const finalTablesToRender = tablesToRender.length > 0 ? tablesToRender : 
    (transformedData?.allMarkdownTables || []);
  
  // Additional fallback: try to extract tables from session data directly
  const sessionTables = finalTablesToRender.length === 0 ? 
    (() => {
      const tables: Array<{ heading: string, table: TableData }> = [];
      if (sessionData?.output?.content) {
        Object.entries(sessionData.output.content).forEach(([tabName, content]) => {
          if (Array.isArray(content)) {
            content.forEach((item: any) => {
              if (item.type === 'markdown' && item.content) {
                const extractedTables = extractTablesWithHeadings(item.content);
                tables.push(...extractedTables);
              }
            });
          }
        });
      }
      return tables;
    })() : [];
  
  const allTablesToRender = finalTablesToRender.length > 0 ? finalTablesToRender : sessionTables;
  
  // Remove duplicate tables based on heading
  const uniqueTables = allTablesToRender.filter((table, index, self) => 
    index === self.findIndex(t => t.heading === table.heading)
  );
  
  // Extract vendor names and totals dynamically (unified logic)
  const vendorNames = extractVendorNames(uniqueTables, tabMarkdown);
  const vendorTotals = extractVendorTotals(uniqueTables, tabMarkdown);
  
  // Use provided vendorTotalsFromSummary or fall back to dynamic extraction
  const finalVendorTotals = vendorTotalsFromSummary && Object.keys(vendorTotalsFromSummary).length > 0 
    ? vendorTotalsFromSummary 
    : vendorTotals;

  return (
    <div className="min-h-screen bg-primary-50 font-['Work_Sans']">
      <div className="mx-auto py-8 pt-0">
        
        {/* Summary Sections */}
          {Object.keys(summarySections).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(summarySections).map(([sectionName, content]) => (
                <div key={sectionName} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <MarkdownRenderer content={content} className="summary-section" />
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="text-gray-500 mb-4">No summary sections found. Debug info:</div>
              <div className="text-sm text-gray-600">
                <div>Tab markdown length: {tabMarkdown?.length || 0}</div>
                <div>Session data has content: {sessionData?.output?.content ? 'Yes' : 'No'}</div>
                <div>Active tab: {activeTab}</div>
                <div>Available tabs: {sessionData?.output?.tabs?.join(', ') || 'None'}</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Vendor Legend - show for multi-vendor scenarios regardless of tab name */}
        {vendorNames.length > 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-8">
            <h2 className="text-lg font-semibold text-primary-900 mb-2">Vendor Legend</h2>
            <ul className="list-disc list-inside text-primary-700 text-sm">
              {vendorNames.map((vendorName: string, idx: number) => (
                <li key={vendorName}><b>Vendor {String.fromCharCode(65 + idx)}</b>: {vendorName}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Vendor Summary Cards - show when totals are available */}
        {Object.keys(finalVendorTotals).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {Object.entries(finalVendorTotals).map(([vendorName, total]) => (
              <StatCard
                key={vendorName}
                title={vendorName}
                value={`₹${total.toLocaleString('en-IN')}`}
                subtitle="Total Quote Value"
              />
            ))}
          </div>
        )}
        
        {/* Render graphs when totals are available */}
        {Object.keys(finalVendorTotals).length > 0 && (
          <VendorBarChart vendorTotalsFromSummary={finalVendorTotals} />
        )}
        
        {/* Show VendorPieCharts when vendor data can be derived */}
        <VendorPieCharts tabData={transformedData?.tab1Data} tables={uniqueTables} markdown={tabMarkdown} />
        
        {/* Render all tables with proper headings */}
        {uniqueTables.length > 0 ? (
          uniqueTables.map((tbl, idx) => (
            <div key={idx} className="mb-10">
              <h3 className="text-lg font-semibold text-primary-900 mb-2 mt-8">
                {tbl.heading}
              </h3>
              <DynamicTable headers={tbl.table.headers} rows={tbl.table.rows} />
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="text-red-500 mb-4">No tables found in the response data.</div>
            <div className="text-sm text-gray-600">
              Please check the data source for this tab.
            </div>
          </div>
        )}
        
        {/* Test session data structure */}
        {/* {sessionData && (
          <div className="mb-10">
            <h3 className="text-lg font-semibold text-primary-900 mb-2 mt-8">Session Data Structure</h3>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <pre className="text-xs overflow-auto">
                {JSON.stringify({
                  hasOutput: !!sessionData.output,
                  hasContent: !!sessionData.output?.content,
                  contentKeys: Object.keys(sessionData.output?.content || {}),
                  tabs: sessionData.output?.tabs || []
                }, null, 2)}
              </pre>
            </div>
          </div>
        )} */}
        
      </div>
  );
};

export { EventAnalysisDashboard };